import { Injectable, inject } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, catchError, map, of } from 'rxjs';
import {
  BioinformaticReportQueryResult,
  DnaSkinGuidelineAllergySensitivityQueryResult,
  DnaSkinGuidelineFaceBlueprintQueryResult,
  DnaSkinGuidelineInsightQueryResult
} from '@interfaces/schema';

/**
 * Report Skin Health Guideline Service.
 *
 * @exports
 * @class ReportSkinHealthGuidelineService
 */
@Injectable()
export class ReportSkinHealthGuidelineService {
  private readonly apollo = inject(Apollo);

  /**
   * Fetch Skin Guideline Allergy Sensitivity.
   *
   * @returns {Observable<DnaSkinGuidelineAllergySensitivityQueryResult>}
   * @memberof ReportSkinHealthGuidelineService
   */
  fetchSkinGuidelineAllergySensitivity(): Observable<DnaSkinGuidelineAllergySensitivityQueryResult> {
    const query = gql`
      query GetSkinGuidlineAllergySensitivityByUser($include: [String]) {
        DNA {
          Skin {
            Guideline {
              AllergySensitivity {
                findByUser(include: $include) {
                  count
                  results {
                    id
                    iconTrait
                    dnaTraitId
                    summaryInsight
                    skinGuidelineAllergySensitivityInsight {
                      id
                      type
                      title
                      insight
                      position
                    }
                    updatedAt
                    createdAt
                  }
                }
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query({
        query,
        variables: {
          include: ['rpx-skin-guideline-allergy-sensitivity-insight']
        },
        fetchPolicy: 'network-only'
      })
      .pipe(
        map(
          ({ data }) =>
            ((data as any)?.DNA?.Skin?.Guideline?.AllergySensitivity
              ?.findByUser ?? {
              count: 0,
              results: []
            }) as DnaSkinGuidelineAllergySensitivityQueryResult
        ),
        catchError(() => {
          return of({
            count: 0,
            results: []
          } as DnaSkinGuidelineAllergySensitivityQueryResult);
        })
      );
  }

  /**
   * Fetch Skin Guideline Face Blueprint.
   *
   * @returns {Observable<DnaSkinGuidelineFaceBlueprintQueryResult>}
   * @memberof ReportSkinHealthGuidelineService
   */
  fetchSkinGuidelineFaceBlueprint(): Observable<DnaSkinGuidelineFaceBlueprintQueryResult> {
    const query = gql`
      query GetSkinGuidlineFaceBlueprintByUser($include: [String]) {
        DNA {
          Skin {
            Guideline {
              FaceBlueprint {
                findByUser(include: $include) {
                  count
                  results {
                    id
                    code
                    name
                    icon
                    skinType
                    scoreStatus
                    summaryInsight
                    skinCharacteristic
                    faceHighlightPointer
                    skinGuidelineFaceBlueprintInsight {
                      id
                      type
                      title
                      insight
                      position
                    }
                    updatedAt
                    createdAt
                  }
                }
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query({
        query,
        variables: {
          include: ['rpx-skin-guideline-face-blueprint-insight']
        },
        fetchPolicy: 'network-only'
      })
      .pipe(
        map(
          ({ data }) =>
            ((data as any)?.DNA?.Skin?.Guideline?.FaceBlueprint?.findByUser ?? {
              count: 0,
              results: []
            }) as DnaSkinGuidelineFaceBlueprintQueryResult
        ),
        catchError(() => {
          return of({
            count: 0,
            results: []
          } as DnaSkinGuidelineFaceBlueprintQueryResult);
        })
      );
  }

  /**
   * Fetch Skin Guideline Skin Insight.
   *
   * @returns {Observable<DnaSkinGuidelineInsightQueryResult>} Observable of skin guideline insight query result.
   * @memberof ReportSkinHealthGuidelineService
   */
  fetchSkinGuidelineSkinInsight(): Observable<DnaSkinGuidelineInsightQueryResult> {
    const query = gql`
      query GetSkinGuidlineInsightByUser($include: [String]) {
        DNA {
          Skin {
            Guideline {
              Insight {
                findByUser(include: $include) {
                  count
                  results {
                    id
                    iconTrait
                    dnaTraitId
                    summaryInsight
                    skinGuidelineInsightDetail {
                      id
                      type
                      title
                      insight
                      position
                    }
                    updatedAt
                    createdAt
                  }
                }
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query({
        query,
        variables: {
          include: ['rpx-skin-guideline-insight-detail']
        },
        fetchPolicy: 'network-only'
      })
      .pipe(
        map(
          ({ data }) =>
            ((data as any)?.DNA?.Skin?.Guideline?.Insight?.findByUser ?? {
              count: 0,
              results: []
            }) as DnaSkinGuidelineInsightQueryResult
        ),
        catchError(() => {
          return of({
            count: 0,
            results: []
          } as DnaSkinGuidelineInsightQueryResult);
        })
      );
  }

  /**
   * Fetch Trait by ID.
   *
   * @param {number} traitId Trait ID to fetch.
   * @returns {Observable<BioinformaticReportQueryResult>} Observable of bioinformatic report query result.
   * @memberof ReportSkinHealthGuidelineService
   */
  fetchTrait(
    traitId: Array<number> | number
  ): Observable<BioinformaticReportQueryResult> {
    const query = gql`
      query findAllReport(
        $where: BioinformaticReportFilter
        $sort: BioinformaticReportQuerySortBy
        $limit: Int
        $offset: Int
      ) {
        Bioinformatic {
          Report {
            findAll(
              where: $where
              sort: $sort
              limit: $limit
              offset: $offset
            ) {
              count
              results {
                id
                score
                dnaScoreTypeGuideline {
                  id
                  mappedScore
                  response
                  riskLevel
                  dScoreType {
                    id
                    name
                    isHigherBetter
                  }
                  dnaScoreTypeId
                }
                panel {
                  id
                  name
                  thumbnail
                }
                genotypes {
                  gene {
                    id
                    name
                    description
                  }
                  types
                }
                traitScore {
                  id
                  rating
                  minScore: from
                  maxScore: to
                  description: interpretation
                  shortInterpretation
                  symptoms
                  dos
                  donts
                }
                trait {
                  id
                  name
                  description
                }
                category {
                  id
                  name
                }
                dnaTrait {
                  id
                  name
                  description
                  guidelineType
                }
                dnaCategory {
                  id
                  name
                  thumbnail
                }
                dnaSubCategory {
                  id
                  name
                }
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query({
        query,
        variables: {
          sort: {},
          limit: Array.isArray(traitId) ? traitId.length : 1,
          offset: 0,
          where: {
            reportType: 'SKIN',
            traits: Array.isArray(traitId)
              ? traitId.map((id) => id.toString())
              : [traitId.toString()]
          }
        },
        fetchPolicy: 'network-only'
      })
      .pipe(
        map(
          ({ data }) =>
            ((data as any)?.Bioinformatic?.Report?.findAll ?? {
              count: 0,
              results: []
            }) as BioinformaticReportQueryResult
        ),
        catchError(() => {
          return of({
            count: 0,
            results: []
          } as BioinformaticReportQueryResult);
        })
      );
  }
}
